import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router";

import { Badge } from "@profitlens/ui/badge";
import { Button } from "@profitlens/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@profitlens/ui/tooltip";
import { useEntitlements, useSession } from "@/features/auth/use-session";
import { cn } from "@/lib/cn";
import { useUiStore } from "@/stores/ui-store";

import { NAV_ITEMS } from "./nav-items";

/** Desktop-only sidebar (>= lg). Mobile uses the bottom tab bar instead. */
export function Sidebar() {
  const { t } = useTranslation();
  const collapsed = useUiStore((state) => state.sidebarCollapsed);
  const toggleSidebar = useUiStore((state) => state.toggleSidebar);
  const { tenant } = useSession();
  const { isPro } = useEntitlements();

  return (
    <aside
      className={cn(
        "sticky top-0 hidden h-dvh shrink-0 flex-col border-r border-border bg-surface transition-[width] lg:flex",
        collapsed ? "w-[72px]" : "w-64",
      )}
    >
      <div
        className={cn(
          "flex h-14 items-center gap-2 border-b border-border px-3",
          collapsed && "justify-center px-0",
        )}
      >
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand text-sm font-bold text-brand-fg">
          PL
        </div>
        {!collapsed ? (
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">{t("app.name")}</p>
            <p className="truncate text-xs text-muted">{tenant?.name ?? t("app.tagline")}</p>
          </div>
        ) : null}
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto p-2">
        {NAV_ITEMS.map((item) => {
          const link = (
            <NavLink
              to={item.to}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
                  isActive
                    ? "bg-brand-soft font-medium text-brand"
                    : "text-muted hover:bg-surface-muted hover:text-foreground",
                  collapsed && "justify-center px-0",
                )
              }
            >
              <item.icon className="h-4 w-4 shrink-0" />
              {collapsed ? null : (
                <>
                  <span className="flex-1 truncate">{t(item.labelKey)}</span>
                  {item.pro ? (
                    <Badge variant={isPro ? "default" : "secondary"}>{t("billing.proBadge")}</Badge>
                  ) : null}
                </>
              )}
            </NavLink>
          );

          return collapsed ? (
            <Tooltip key={item.to}>
              <TooltipTrigger asChild>{link}</TooltipTrigger>
              <TooltipContent side="right">{t(item.labelKey)}</TooltipContent>
            </Tooltip>
          ) : (
            <div key={item.to}>{link}</div>
          );
        })}
      </nav>

      <div className="border-t border-border p-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleSidebar}
          className={cn("w-full", collapsed && "justify-center px-0")}
          aria-label={collapsed ? t("nav.expand") : t("nav.collapse")}
        >
          {collapsed ? (
            <PanelLeftOpen className="h-4 w-4" />
          ) : (
            <>
              <PanelLeftClose className="h-4 w-4" />
              {t("nav.collapse")}
            </>
          )}
        </Button>
      </div>
    </aside>
  );
}
